import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { FormField, FormService } from "@shared/services/FormServices";
import { TabsContent } from "@shared/ui/tabs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAreaList, usePremiseList, useSubAreaList } from "../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

interface PremisesTabProps {
  selectedPremises: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  onPremiseListChange?: (premiseList: any[]) => void;
}

export const PremisesTab: React.FC<PremisesTabProps> = ({
  selectedPremises,
  onSelectionChange,
  onPremiseListChange,
}) => {
  const { data: areaList } = useAreaList();
  const [premisesSearch, setPremisesSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState("all_areas");
  const [currentPage, setCurrentPage] = useState(1);
  const [subAreaListConfig, setSubArea] = useState<any[]>([]);

  const [queryParams, setQueryParams] = useState({
    page: currentPage,
    search_data: premisesSearch.length > 2 ? premisesSearch : "",
    area: [],
    sub_area: [],
  });

  const { data: premiseList } = usePremiseList(queryParams);
  const premiselist = premiseList?.result;
  const filterFields: FormField[] = [
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      placeholder: "Select Area",
      options: areaList?.map((area: any) => ({
        value: area.id,
        label: area.name,
      })),
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        // error: "text-red-600 text-sm mt-1",
        // select:
        //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        // helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "sub_area",
      label: "Sub Area",
      type: "multi-select",
      placeholder: "Select Sub Area",
      options: subAreaListConfig,
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // input: "w-full h-12 px-4 border border-gray-300 rounded-lg",
        // error: "text-red-600 text-sm mt-1",
        // select:
        //   "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
        // helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];

  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = form.watch();

  const { data: subAreaList = [] } = useSubAreaList(
    watchedValues.area && Array.isArray(watchedValues.area)
      ? watchedValues.area
      : []
  );

  useEffect(() => {
    if (onPremiseListChange && premiselist) {
      onPremiseListChange(premiselist);
    }
  }, [premiselist, onPremiseListChange]);

  useEffect(() => {
    setSubArea(
      subAreaList.map((subCategory: any) => ({
        value: subCategory.id,
        label: subCategory.name,
      }))
    );
  }, [subAreaList]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();

    setQueryParams((prev) => ({
      ...prev,
      sub_area: formValues.sub_area || [],
      page: 1,
    }));
  };

  const handleSearchChange = useCallback((search: string) => {
    setCurrentPage(1);

    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search_data: search.length > 2 ? search : "",
    }));
  }, []);

  const handleSelectionChange = (newSelectedRows: any) => {
    onSelectionChange(newSelectedRows);
  };

  const premisesColumns = [
    {
      key: "name",
      header: "Premise",
      sortable: true,
    },
    {
      key: "area", // Unique key
      header: "Area",
      sortable: true,
      render: (_, row) => row.hierarchyNames?.area,
    },
    {
      key: "subArea", // Unique key
      header: "Subarea",
      sortable: true,
      render: (_, row) => row.hierarchyNames?.subArea,
    },
    {
      key: "installedMeterCount",
      header: "Consumer Count",
      sortable: true,
      render: (value: number) => value?.toLocaleString(),
    },
  ];

  const handleSearch = useCallback(
    (searchTerm: string, paramName: string) => {
      handleSearchChange(searchTerm);
    },
    [handleSearchChange]
  );

  const paginationConfig = useMemo(() => {
    if (!premiselist) return undefined;
    
    const totalItems = premiseList.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = ((currentPage - 1) * PAGE_SIZE) + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    };
  }, [premiselist, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setQueryParams((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const filters = (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterSubmit}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          classes: {
            form: " gap-4",
          },
        }}
      />
    </div>
  );

  return (
    <TabsContent value="premises" className="mt-0 max-h-[80vh] overflow-x-scroll">
      <GenericTable
        data={premiselist ?? []}
        columns={premisesColumns}
        rowKey="code"
        selectable={true}
        selectedRows={selectedPremises}
        onSelectionChange={handleSelectionChange}
        searchPlaceholder="Search premises..."
        onSearch={handleSearch}
        emptyMessage="No premises match your search criteria"
        onPageChange={handlePageChange}
        pagination={paginationConfig}
        filterPanel={filters}
      />
    </TabsContent>
  );
};
